// @flow
import { useState, type Node, useMemo, Fragment, useEffect } from 'react';
import styled from '@emotion/styled';
import loadable from '@loadable/component';
import { allCountries as defaultI18nCountries } from '@tablekit/phone-input/lib/defaultI18nCountries';
import { animated, useTransition } from 'react-spring/web.cjs';
import { SPACING, COLORS } from '@tablekit/theme';
import { Z_INDICES } from 'utils/theme';
import type { AutoModalConfigType } from '../types';
import { getPageContext, getPageLocale } from '../PageContext';
import { GEO_LOCATION_STORAGE_KEY } from '../../utils/constants';
import {
  ModalContext,
  loadCachedModal,
  getModal,
  type ModalContextType
} from './context';

const Tint = animated(styled.div`
  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  opacity: 0;
  z-index: ${Z_INDICES.MODAL - 1};
  background-color: ${COLORS.GRAY_TRANSLUCENT.L6};
`);

const Positioner = animated(styled.div`
  position: fixed;
  z-index: ${Z_INDICES.MODAL};

  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  overflow: visible;
  // catches an issue with password managers trying to autofill the page and
  // this expands the modal outside the desired screen width
  max-width: 100vw;
`);

const { Provider } = ModalContext;

const Modal = loadable(() => import('./Modal'));

const AUTO_MODAL_VISIT_TIME_KEY = 'tablesolution-auto-modal-timeout';
export const AUTO_MODAL_SHOWN_KEY = 'tablesolution-auto-modal-shown';

let autoShowModalTimeout;
const TWENTY_FOUR_HOURS = 86400000;
const ONE_MINUTE = 60000;

export default function ModalContextProvider({
  autoModalConfig,
  currentStory,
  values = {},
  children
}: {
  autoModalConfig?: AutoModalConfigType,
  children: Node,
  currentStory?: string,
  // most of the time this should be empty unless we've pre-loaded a modal from page-context
  values?: ModalContextType
}) {
  const locale = getPageLocale();
  const { phoneInputSettings, phoneInputSettingsMap } = getPageContext();
  const [i18nCountries, setI18nCountries] = useState<any[]>(
    defaultI18nCountries
  );
  let browserValue = '';
  if (typeof window !== 'undefined') {
    browserValue = localStorage.getItem(GEO_LOCATION_STORAGE_KEY);
  }
  const [defaultCountry, setDefaultCountry] = useState(browserValue || '');
  useEffect(() => {
    const importPath = `../../i18n/${locale.split('-')[0]}.json`;
    // $FlowFixMe ignoring flows insistence on non-dynamic import
    import(importPath)
      .then(({ default: countryTranslations }) => {
        const mappedCountries = defaultI18nCountries.map((country) => ({
          ...country,
          name:
            countryTranslations[`country.${country.shortName.toLowerCase()}`]
        }));
        const phoneSettings =
          phoneInputSettings ||
          (phoneInputSettingsMap && phoneInputSettingsMap[locale]);
        if (phoneSettings) {
          const suggestedCountries = phoneSettings.suggested_countries
            .split(',')
            .map((code) => code.toLowerCase().trim());
          if (!defaultCountry) {
            setDefaultCountry(suggestedCountries[0] || '');
          }
          if (suggestedCountries.length === 0) {
            setI18nCountries(mappedCountries);
          } else {
            setI18nCountries([
              {
                label: phoneSettings.suggested_header,
                options: suggestedCountries.map((code) =>
                  mappedCountries.find(
                    (country) => country.shortName.toLowerCase() === code
                  )
                )
              },
              {
                label: phoneSettings.all_countries_header,
                options: mappedCountries.filter(
                  (country) =>
                    suggestedCountries.indexOf(
                      country.shortName.toLowerCase()
                    ) === -1
                )
              }
            ]);
          }
        } else {
          setI18nCountries(mappedCountries);
        }
      })
      .catch(() => {
        setI18nCountries(defaultI18nCountries);
      });
  }, [locale]);
  const clearAutoModal = () => {
    if (!autoModalConfig) return;
    try {
      localStorage.setItem(AUTO_MODAL_SHOWN_KEY, 'true');
    } catch (e) {
      console.error(e);
    }
    clearTimeout(autoShowModalTimeout);
  };
  const [openModals, setOpenModals] = useState([]);
  const [modalDynamicOptions, setModalDynamicOptions] = useState({});
  const value = useMemo(
    () => ({
      openModals,
      toggleModal: (
        modalId: string,
        isAutoModal?: boolean,
        dynamicOptions?: Object
      ) => {
        clearAutoModal();
        const index = openModals.indexOf(modalId);
        const newModalDynamicOptions = {
          ...modalDynamicOptions,
          [modalId]: undefined
        };
        if (index === -1) {
          if (window.posthog) {
            window.posthog.capture(
              isAutoModal ? 'Automatic Popup Modal Opened' : 'Modal Opened',
              {
                event_category: modalId
              }
            );
          }
          if (window.umami) {
            window.umami.trackEvent(
              isAutoModal ? 'Automatic Popup Modal Opened' : 'Modal Opened',
              { event_category: modalId }
            );
          }

          if (dynamicOptions) {
            newModalDynamicOptions[modalId] = dynamicOptions;
          }
          if (values[modalId]) {
            setOpenModals([...openModals, modalId]);
          } else {
            loadCachedModal(modalId).then(() => {
              setOpenModals([...openModals, modalId]);
            });
          }
        } else {
          if (window.posthog) {
            window.posthog.capture('Modal Closed', {
              event_category: modalId
            });
          }
          if (window.umami) {
            window.umami.trackEvent('Modal Closed', {
              event_category: modalId
            });
          }
          setOpenModals([
            ...openModals.slice(0, index),
            ...openModals.slice(index + 1)
          ]);
        }
        setModalDynamicOptions(newModalDynamicOptions);
      }
    }),
    [openModals]
  );
  // manual trigger API, doesn't really need to be hidden as exposing it isn't a risk
  // usage is `window.modalTrigger('en/modals/modal-path');`
  if (typeof window !== 'undefined') {
    window.modalTrigger = (modalId) => value.toggleModal(modalId, false);
  }

  useEffect(() => {
    const onEscListener = (event) => {
      if (event.keyCode === 27 && openModals.length) {
        value.toggleModal(openModals[openModals.length - 1]);
      }
    };
    window.addEventListener('keydown', onEscListener);
    return () => {
      window.removeEventListener('keydown', onEscListener);
    };
  }, [value]);

  useEffect(() => {
    if (
      autoModalConfig &&
      autoModalConfig.cancel_stories.find(
        ({ url, cached_url: cachedUrl }) =>
          url === currentStory || cachedUrl === currentStory
      )
    ) {
      clearAutoModal();
    }
    try {
      let isShown = localStorage.getItem(AUTO_MODAL_SHOWN_KEY);
      let firstVisitTime = localStorage.getItem(AUTO_MODAL_VISIT_TIME_KEY);
      if (firstVisitTime) {
        firstVisitTime = parseInt(firstVisitTime, 10);
      }
      if (autoModalConfig && isShown && firstVisitTime) {
        const elapsedTime = new Date().getTime() - firstVisitTime;
        if (elapsedTime / TWENTY_FOUR_HOURS >= 1) {
          isShown = false;
          firstVisitTime = undefined;
          localStorage.removeItem(AUTO_MODAL_SHOWN_KEY);
        }
      }
      if (autoModalConfig && !isShown) {
        if (!firstVisitTime) {
          firstVisitTime = new Date().getTime();
          localStorage.setItem(AUTO_MODAL_VISIT_TIME_KEY, `${firstVisitTime}`);
        }
        const now = new Date().getTime();
        const timeout = autoModalConfig.timeout * ONE_MINUTE;
        if (now - firstVisitTime >= timeout) {
          value.toggleModal(
            autoModalConfig.modal.cached_url || autoModalConfig.modal.url,
            true
          );
        } else {
          autoShowModalTimeout = setTimeout(() => {
            value.toggleModal(
              autoModalConfig.modal.cached_url || autoModalConfig.modal.url,
              true
            );
          }, timeout - (now - firstVisitTime));
        }
      }
    } catch (e) {
      console.error(e);
    }
    return () => {
      clearTimeout(autoShowModalTimeout);
    };
  }, [value]);

  const animationStates = useTransition(openModals, null, {
    from: { isOpen: 0 },
    enter: {
      isOpen: 1
    },
    leave: {
      isOpen: 0
    }
  });
  return (
    <Provider value={value}>
      {children}
      {animationStates.map(({ item, key, props: styles }, index) => {
        const config = getModal(item, values);
        if (!config) return null;
        return (
          <Fragment key={key}>
            <Positioner
              index={index}
              onClick={() => value.toggleModal(item)}
              style={{
                transform: styles.isOpen.interpolate(
                  (v) => `translateY(-${(1 - v) * parseInt(SPACING.L6, 10)}px)`
                ),
                opacity: styles.isOpen
              }}
            >
              <Modal
                config={config}
                id={item}
                depth={index}
                closeModal={value.toggleModal}
                i18nCountries={i18nCountries}
                defaultCountry={defaultCountry || 'JP'}
                options={modalDynamicOptions[item]}
              />
            </Positioner>
            <Tint
              style={{
                opacity: styles.isOpen.interpolate({
                  range: [0, 0.4],
                  output: [0, 1]
                })
              }}
            />
          </Fragment>
        );
      })}
    </Provider>
  );
}
